/**
 * B728 (Eric 2026-02-13) — Modal de réglages Whisper / AssemblyAI per-vidéo.
 *
 * Ouvert via les boutons « Réglages Whisper » et « Réglages Assembly AI » de
 * la barre d'actions de l'onglet Transcriptions. Les réglages sont sauvegardés
 * sur la vidéo courante uniquement (video.whisper_settings / video.assemblyai_settings)
 * et consommés à la PROCHAINE transcription/diarisation lancée sur cette vidéo.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, FloppyDisk, ArrowCounterClockwise, Lightning, Sparkle, GitDiff, BookBookmark, Target, Stack } from "@phosphor-icons/react";

const WHISPER_LANGS = [
  { value: "auto", label: "Auto-détection" },
  { value: "fr", label: "Français" },
  { value: "en", label: "Anglais" },
  { value: "es", label: "Espagnol" },
  { value: "de", label: "Allemand" },
  { value: "it", label: "Italien" },
  { value: "pt", label: "Portugais" },
];
const AAI_LANGS = [
  { value: "fr", label: "Français" },
  { value: "en", label: "Anglais" },
  { value: "es", label: "Espagnol" },
  { value: "de", label: "Allemand" },
  { value: "it", label: "Italien" },
  { value: "pt", label: "Portugais" },
];

const TranscriptionSettingsModal = ({ open, engine, api, videoId, videoDuration = 0, lang, onClose, onRerun }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [whisper, setWhisper] = useState(null);
  const [aai, setAai] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  // B728 enhancement (Eric 2026-02-13) — A/B test des prompts Whisper.
  const [abOpen, setAbOpen] = useState(false);
  const [abPromptB, setAbPromptB] = useState("");
  const [abSampleSec, setAbSampleSec] = useState(30);
  const [abStartSec, setAbStartSec] = useState(0);
  const [abLoading, setAbLoading] = useState(false);
  const [abResult, setAbResult] = useState(null); // {a: {text, segments}, b: {...}, cost_estimate_usd}
  // B728 enhancement (Eric 2026-02-13) — Prompt library + batch test.
  const [library, setLibrary] = useState([]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveTagsRaw, setSaveTagsRaw] = useState("");
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchVideos, setBatchVideos] = useState([]); // list of {id, title} available
  const [batchSelected, setBatchSelected] = useState([]); // selected video ids
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [autoPickLoading, setAutoPickLoading] = useState(false);
  // Vincent B778 v3 (Eric 2026-02-18 SPEC STRICK PROD) — Silence Preview Meter.
  // Mini barre horizontale sous le champ Offset A/B qui montre la densité
  // de parole (0=silence, 1=zone dense) par bucket sur toute la vidéo.
  const [energyMap, setEnergyMap] = useState(null); // {duration, buckets: [{start, end, score}], source}
  // B728 enhancement (Eric 2026-02-13) — Prompt version history.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  // B728 v4 (Eric 2026-02-13) — History diff preview before restore.
  const [historyDiffPreview, setHistoryDiffPreview] = useState(null); // {entry, currentPrompt}

  useEffect(() => {
    if (!open || !videoId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [settingsRes, suggestionsRes] = await Promise.all([
          axios.get(`${api}/api/videos/${videoId}/transcription-settings`, { withCredentials: true }),
          engine === "whisper"
            ? axios.get(`${api}/api/videos/${videoId}/whisper-prompt-suggestions`, { withCredentials: true }).catch(() => ({ data: { suggestions: [] } }))
            : Promise.resolve({ data: { suggestions: [] } }),
        ]);
        if (cancelled) return;
        const data = settingsRes.data;
        // Whisper "language" = "" pour auto — mappe vers "auto" côté UI.
        const w = { ...data.whisper };
        if (!w.language) w.language = "auto";
        setWhisper(w);
        setAai({ ...data.assemblyai });
        setDefaults({ whisper: data.whisper_defaults, assemblyai: data.assemblyai_defaults });
        setSuggestions(suggestionsRes.data?.suggestions || []);
      } catch (err) {
        toast.error(
          lang === "fr"
            ? `Erreur : ${err.response?.data?.detail || err.message}`
            : `Error: ${err.response?.data?.detail || err.message}`
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, videoId, api, lang, engine]);

  // B728 enhancement (Eric 2026-02-13) — Load prompt library on open (whisper only).
  useEffect(() => {
    if (!open || engine !== "whisper") return;
    (async () => {
      try {
        const { data } = await axios.get(`${api}/api/whisper-prompt-library`, { withCredentials: true });
        setLibrary(data?.prompts || []);
      } catch (err) {
        // silent
      }
    })();
  }, [open, engine, api]);

  // B728 enhancement (Eric 2026-02-13) — Silently pre-fill A/B start offset
  // with the densest transcript zone so testing feels effortless.
  useEffect(() => {
    if (!open || engine !== "whisper" || !videoId) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${api}/api/videos/${videoId}/whisper-ab-densest-zone?duration=30`,
          { withCredentials: true }
        );
        if (data?.reason === "densest_window" && typeof data.start_seconds === "number") {
          setAbStartSec(data.start_seconds);
        }
      } catch (err) {
        // silent — offset stays at 0
      }
    })();
  }, [open, engine, videoId, api]);

  // Vincent B778 v3 (Eric 2026-02-18 SPEC STRICK PROD) — Fetch la carte
  // d'énergie sonore (densité de parole par bucket) pour rendre le mini
  // « Silence Preview Meter » sous le champ Offset A/B.
  useEffect(() => {
    if (!open || engine !== "whisper" || !videoId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${api}/api/videos/${videoId}/audio-energy-map?buckets=80`,
          { withCredentials: true }
        );
        if (!cancelled && data) setEnergyMap(data);
      } catch (_err) {
        // silent — meter reste caché
      }
    })();
    return () => { cancelled = true; };
  }, [open, engine, videoId, api]);

  // B728 enhancement (Eric 2026-02-13) — Load prompt version history on open.
  useEffect(() => {
    if (!open || engine !== "whisper" || !videoId) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${api}/api/videos/${videoId}/whisper-prompt-history`,
          { withCredentials: true }
        );
        setHistory(data?.history || []);
      } catch (err) {
        // silent
      }
    })();
  }, [open, engine, videoId, api]);

  const openHistoryPreview = (entry) => {
    // B728 v4 (Eric 2026-02-13) — Preview diff avant restore. Empêche les
    // rollbacks accidentels : l'admin voit d'abord ce qui va changer.
    setHistoryDiffPreview({ entry, currentPrompt: whisper?.prompt || "" });
    setHistoryOpen(false);
  };

  const confirmRestoreHistorical = () => {
    if (!historyDiffPreview) return;
    const entry = historyDiffPreview.entry;
    setWhisper((w) => ({ ...w, prompt: entry.prev_prompt }));
    toast.success(
      lang === "fr"
        ? `Restauré : version du ${new Date(entry.saved_at).toLocaleString("fr-CA")}`
        : `Restored: ${new Date(entry.saved_at).toLocaleString("en-CA")}`,
      { duration: 3500 }
    );
    setHistoryDiffPreview(null);
  };

  const autoPickDenseZone = async () => {
    setAutoPickLoading(true);
    try {
      const { data } = await axios.get(
        `${api}/api/videos/${videoId}/whisper-ab-densest-zone?duration=${abSampleSec}`,
        { withCredentials: true }
      );
      setAbStartSec(data.start_seconds || 0);
      if (data.density_wps) {
        toast.success(
          lang === "fr"
            ? `🎯 Zone dense trouvée à ${data.start_seconds}s (${data.density_wps} mots/s)`
            : `🎯 Densest at ${data.start_seconds}s (${data.density_wps} wps)`,
          { duration: 3500 }
        );
      } else {
        toast.info(
          lang === "fr"
            ? "Transcription absente — utilise l'offset 0."
            : "No transcript — offset 0.",
          { duration: 3500 }
        );
      }
    } catch (err) {
      toast.error(`Erreur : ${err.response?.data?.detail || err.message}`);
    } finally {
      setAutoPickLoading(false);
    }
  };

  const applyLibraryPrompt = async (item, target /* "A" | "B" */) => {
    if (target === "B") {
      setAbPromptB(item.prompt);
    } else {
      setWhisper((w) => ({ ...w, prompt: item.prompt }));
    }
    setLibraryOpen(false);
    toast.info(
      lang === "fr" ? `Prompt « ${item.name} » injecté (${target})` : `Prompt "${item.name}" applied (${target})`,
      { duration: 2500 }
    );
    // Fire-and-forget usage increment
    axios.post(`${api}/api/whisper-prompt-library/${item.id}/use`, {}, { withCredentials: true }).catch(() => {});
  };

  const saveToLibrary = async ({ winnerOfAb = false } = {}) => {
    if (!saveName.trim() || !(whisper?.prompt || "").trim()) {
      toast.error(lang === "fr" ? "Nom et prompt requis" : "Name and prompt required");
      return;
    }
    try {
      const tags = saveTagsRaw.split(",").map((t) => t.trim()).filter(Boolean);
      const { data } = await axios.post(
        `${api}/api/whisper-prompt-library`,
        { name: saveName, prompt: whisper.prompt, tags, winner_of_ab: winnerOfAb },
        { withCredentials: true }
      );
      setLibrary((prev) => [data, ...prev]);
      setSaveDialogOpen(false);
      setSaveName("");
      setSaveTagsRaw("");
      toast.success(lang === "fr" ? "Sauvegardé dans la bibliothèque" : "Saved to library");
    } catch (err) {
      toast.error(`Erreur : ${err.response?.data?.detail || err.message}`);
    }
  };

  const deleteLibraryPrompt = async (id) => {
    try {
      await axios.delete(`${api}/api/whisper-prompt-library/${id}`, { withCredentials: true });
      setLibrary((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(`Erreur : ${err.response?.data?.detail || err.message}`);
    }
  };

  // B728 enhancement (Eric 2026-02-13) — Batch prompt test setup.
  const openBatchPanel = async () => {
    setBatchOpen(true);
    try {
      const { data } = await axios.get(`${api}/api/videos`, { withCredentials: true });
      const items = data?.items || data || [];
      setBatchVideos(items.filter((v) => v.id !== videoId && v.pipeline_stage === "complete").slice(0, 30));
    } catch (err) {
      // silent
    }
  };

  const toggleBatchSelected = (id) => {
    setBatchSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : (prev.length >= 4 ? prev : [...prev, id]));
  };

  const runBatchTest = async () => {
    if (batchSelected.length === 0) return;
    setBatchLoading(true);
    setBatchResult(null);
    const toastId = toast.loading(
      lang === "fr" ? `🎯 Test batch en cours (${batchSelected.length} vidéos)…` : `🎯 Batch testing (${batchSelected.length} videos)…`,
      { duration: Infinity }
    );
    try {
      const { data } = await axios.post(
        `${api}/api/videos/batch-whisper-prompt-test`,
        {
          video_ids: [videoId, ...batchSelected],
          prompt_a: whisper.prompt,
          prompt_b: abPromptB || "",
          sample_seconds: abSampleSec,
          language: whisper.language === "auto" ? "fr" : (whisper.language || "fr"),
        },
        { withCredentials: true, timeout: 300000 }
      );
      setBatchResult(data);
      toast.success(
        lang === "fr"
          ? `Batch terminé (coût ≈ ${data.cost_estimate_usd?.toFixed?.(4)} $)`
          : `Batch done (~$${data.cost_estimate_usd?.toFixed?.(4)})`,
        { id: toastId, duration: 5000 }
      );
    } catch (err) {
      toast.error(`Erreur : ${err.response?.data?.detail || err.message}`, { id: toastId, duration: 8000 });
    } finally {
      setBatchLoading(false);
    }
  };

  // Rerun handler shared by "Save & rerun now" — kicks off the correct pipeline.
  // Vincent B779 (Eric 2026-02-18 SPEC STRICK PROD) — Accepte options
  // anti-hallucination per-run pour désactiver ou desserrer le dedup.
  const triggerRerun = async (dedupOpts = {}) => {
    const endpoint = engine === "whisper"
      ? `${api}/api/videos/${videoId}/rerun-whisper`
      : `${api}/api/videos/${videoId}/speakers/reprocess`;
    // rerun-whisper body wants use_diarisation/use_facial_id from current video settings ;
    // we pass empty and let the backend keep the existing toggles.
    const body = { ...dedupOpts };
    await axios.post(endpoint, body, { withCredentials: true, timeout: 60000 });
  };

  // Vincent B779 (Eric 2026-02-18 SPEC STRICK PROD) — Re-transcrire une
  // vidéo en désactivant le dedup anti-hallucination. Utile quand une
  // vidéo légitime (coaching / voix douce) a perdu 80 %+ de segments à
  // cause du seuil Jaccard trop agressif. `dedup_override` est ensuite
  // effacé automatiquement par le pipeline à la fin du run.
  const rerunSansDedup = async () => {
    const confirmMsg = lang === "fr"
      ? "Re-transcrire cette vidéo SANS le filtre anti-hallucination ?\n\nUtilise cette option si le diagnostic a montré `aggressive_dedup` et que la transcription précédente semble trop courte.\n\nCela va :\n  • Effacer la transcription Whisper + fusionnée\n  • Relancer Whisper sans dedup (30 s à quelques minutes)\n  • Restaurer TOUS les segments produits par Whisper"
      : "Re-transcribe WITHOUT the anti-hallucination filter?\n\nUse this if the diagnostic reported `aggressive_dedup` and the previous transcription looks too short.\n\nErases Whisper + merged, re-runs Whisper with dedup off.";
    // eslint-disable-next-line no-alert -- CRA lint flags window.confirm; safe here.
    if (!window.confirm(confirmMsg)) return;
    setRerunning(true);
    const toastId = toast.loading(
      lang === "fr" ? "🎧 Relance Whisper sans dedup…" : "🎧 Re-running Whisper without dedup…",
      { duration: Infinity }
    );
    try {
      await triggerRerun({ skip_dedup: true });
      toast.success(
        lang === "fr"
          ? "🎧 Whisper relancé sans dedup — tous les segments seront conservés"
          : "🎧 Whisper re-run without dedup — all segments will be kept",
        { id: toastId, duration: 8000 }
      );
      onRerun?.();
      onClose?.();
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`,
        { id: toastId, duration: 8000 }
      );
    } finally {
      setRerunning(false);
    }
  };

  const doSave = async () => {
    const settings = engine === "whisper" ? { ...whisper } : { ...aai };
    if (engine === "whisper" && settings.language === "auto") settings.language = "";
    await axios.patch(
      `${api}/api/videos/${videoId}/transcription-settings`,
      { engine, settings },
      { withCredentials: true }
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await doSave();
      toast.success(
        lang === "fr"
          ? "Réglages enregistrés (appliqués à la prochaine transcription)"
          : "Settings saved (applied on next transcription)"
      );
      onClose?.();
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`
      );
    } finally {
      setSaving(false);
    }
  };

  // B728 enhancement (Eric 2026-02-13) — "Enregistrer & relancer maintenant".
  const saveAndRerun = async () => {
    const confirmMsg = engine === "whisper"
      ? (lang === "fr"
        ? "Enregistrer les réglages ET relancer la transcription Whisper immédiatement ?\n\nCela va :\n  • Sauver les réglages\n  • Effacer la transcription Whisper + fusionnée\n  • Relancer l'analyse audio (30 s à quelques minutes)"
        : "Save settings AND re-run Whisper transcription now?\n\nErases Whisper + merged. Re-runs pipeline.")
      : (lang === "fr"
        ? "Enregistrer les réglages ET relancer la diarisation Assembly AI immédiatement ?\n\nCela va :\n  • Sauver les réglages\n  • Relancer la ré-identification des locuteurs (30 s à 2 min)"
        : "Save settings AND re-run Assembly AI diarization now?");
    // eslint-disable-next-line no-alert -- CRA lint flags window.confirm; safe here.
    if (!window.confirm(confirmMsg)) return;
    setRerunning(true);
    const toastId = toast.loading(
      lang === "fr" ? "💾 Sauvegarde & relance…" : "💾 Saving & re-running…",
      { duration: Infinity }
    );
    try {
      await doSave();
      await triggerRerun();
      toast.success(
        lang === "fr"
          ? (engine === "whisper"
            ? "🎧 Réglages sauvés & Whisper relancé en arrière-plan"
            : "🎧 Réglages sauvés & Assembly AI relancé en arrière-plan")
          : "Saved & re-run started",
        { id: toastId, duration: 8000 }
      );
      onRerun?.();
      onClose?.();
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`,
        { id: toastId, duration: 8000 }
      );
    } finally {
      setRerunning(false);
    }
  };

  const applySuggestion = (sugg) => {
    setWhisper((w) => ({ ...w, prompt: sugg.prompt || "" }));
    toast.info(
      lang === "fr" ? `Prompt appliqué : ${sugg.label}` : `Prompt applied: ${sugg.label}`,
      { duration: 3000 }
    );
  };

  // B728 enhancement (Eric 2026-02-13) — A/B test : lance l'endpoint qui
  // transcrit un court extrait avec les deux prompts et retourne les 2 textes.
  //
  // Vincent B778 v2 (Eric 2026-02-18 SPEC STRICK PROD) — Auto-retry
  // silencieux à `start_seconds + 60` UNE fois si Whisper hallucine
  // (silence dominant / boucle de tokens). Économise à Vincent un clic
  // manuel pour tomber sur un extrait parlant. Si le 2ᵉ essai halluciné
  // aussi → on garde le 2ᵉ résultat avec la bannière (message plus fort
  // « essayez manuellement un autre offset »).
  const _callAbTest = async (startSeconds) => {
    const { data } = await axios.post(
      `${api}/api/videos/${videoId}/whisper-prompt-ab-test`,
      {
        prompt_a: whisper.prompt,
        prompt_b: abPromptB,
        sample_seconds: abSampleSec,
        start_seconds: startSeconds,
        language: whisper.language === "auto" ? "fr" : (whisper.language || "fr"),
      },
      { withCredentials: true, timeout: 180000 }
    );
    return data;
  };

  const runAbTest = async () => {
    if (!(whisper?.prompt || "").trim() || !abPromptB.trim()) {
      toast.error(lang === "fr" ? "Les deux prompts sont requis" : "Both prompts required");
      return;
    }
    setAbLoading(true);
    setAbResult(null);
    const toastId = toast.loading(
      lang === "fr" ? `🎧 Transcription A/B (${abSampleSec}s × 2)…` : `🎧 A/B transcribing (${abSampleSec}s × 2)…`,
      { duration: Infinity }
    );
    try {
      let data = await _callAbTest(abStartSec);
      const _isHall = (r) => !!(r?.a?.hallucination_detected || r?.b?.hallucination_detected);
      // B778 v2 — Auto-retry offset : silence détecté ⇒ +60s.
      const _videoDurationSec = Number(videoDuration || 0);
      const _retryStart = abStartSec + 60;
      const _canRetry =
        _isHall(data)
        && _videoDurationSec > 0
        && _retryStart + abSampleSec <= _videoDurationSec;
      if (_canRetry) {
        toast.loading(
          lang === "fr"
            ? `⚠️ Silence détecté à ${abStartSec}s — nouvel essai automatique à ${_retryStart}s…`
            : `⚠️ Silence detected at ${abStartSec}s — auto-retrying at ${_retryStart}s…`,
          { id: toastId, duration: Infinity }
        );
        try {
          const data2 = await _callAbTest(_retryStart);
          // Marque le résultat comme issu de la retry pour que le frontend
          // affiche un message ambre approprié + l'offset réellement testé.
          data2._auto_retried = true;
          data2._original_start_seconds = abStartSec;
          data2._retry_start_seconds = _retryStart;
          data = data2;
          // Met à jour le champ start_seconds visible dans le UI pour que
          // la ré-exécution manuelle démarre au bon endroit si l'utilisateur
          // veut relancer.
          setAbStartSec(_retryStart);
        } catch (_retryErr) {
          // Si la 2ᵉ tentative échoue, on garde le 1er résultat mais on
          // laisse la bannière d'hallucination visible pour information.
          data._retry_failed = true;
        }
      }
      setAbResult(data);
      const _stillHall = _isHall(data);
      if (_stillHall && data._auto_retried) {
        toast.error(
          lang === "fr"
            ? `A/B relancé à ${_retryStart}s — toujours halluciné. Essayez un autre offset manuellement.`
            : `A/B retried at ${_retryStart}s — still hallucinated. Try a different offset manually.`,
          { id: toastId, duration: 8000 }
        );
      } else if (data._auto_retried) {
        toast.success(
          lang === "fr"
            ? `A/B relancé automatiquement à ${_retryStart}s (coût ≈ ${(2 * (data.cost_estimate_usd || 0))?.toFixed?.(4) ?? "?"} $)`
            : `A/B auto-retried at ${_retryStart}s (~$${(2 * (data.cost_estimate_usd || 0))?.toFixed?.(4) ?? "?"})`,
          { id: toastId, duration: 6000 }
        );
      } else {
        toast.success(
          lang === "fr"
            ? `A/B prêt (coût ≈ ${data.cost_estimate_usd?.toFixed?.(4) ?? "?"} $)`
            : `A/B ready (~$${data.cost_estimate_usd?.toFixed?.(4) ?? "?"})`,
          { id: toastId, duration: 5000 }
        );
      }
    } catch (err) {
      toast.error(
        lang === "fr"
          ? `Erreur : ${err.response?.data?.detail || err.message}`
          : `Error: ${err.response?.data?.detail || err.message}`,
        { id: toastId, duration: 8000 }
      );
    } finally {
      setAbLoading(false);
    }
  };

  // Word-level diff : renvoie un array de tokens {text, kind: "same"|"add"|"del"|"sub"}
  // pour l'affichage colorisé côte à côte. Algo :
  //   1. LCS naïf sur split whitespace (suffisant pour extraits ≤60s)
  //   2. Post-pass : détecte les paires del→add adjacentes → substitution "sub"
  //      avec {text: oldWord, replacement: newWord} pour un rendu lisible sur
  //      les corrections de jargon (ex: "réer" → "REER").
  const diffWords = (aStr, bStr) => {
    const A = (aStr || "").split(/(\s+)/);
    const B = (bStr || "").split(/(\s+)/);
    const m = A.length, n = B.length;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i--) {
      for (let j = n - 1; j >= 0; j--) {
        dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
    const raw = [];
    let i = 0, j = 0;
    while (i < m && j < n) {
      if (A[i] === B[j]) { raw.push({ text: A[i], kind: "same" }); i++; j++; }
      else if (dp[i + 1][j] >= dp[i][j + 1]) { raw.push({ text: A[i], kind: "del" }); i++; }
      else { raw.push({ text: B[j], kind: "add" }); j++; }
    }
    while (i < m) { raw.push({ text: A[i++], kind: "del" }); }
    while (j < n) { raw.push({ text: B[j++], kind: "add" }); }
    // Post-pass : del suivi immédiatement (avec ou sans whitespace neutre) d'un
    // add → substitution. Rend le diff naturel pour corrections de jargon.
    const merged = [];
    for (let k = 0; k < raw.length; k++) {
      const cur = raw[k];
      // Skip pure whitespace tokens (they're never del/add of substance)
      const next = raw[k + 1];
      const nextNext = raw[k + 2];
      // Case 1: del then whitespace-same then add → substitution (words replacement)
      if (
        cur.kind === "del" && cur.text.trim() &&
        next && next.kind === "same" && !next.text.trim() &&
        nextNext && nextNext.kind === "add" && nextNext.text.trim()
      ) {
        merged.push({ text: cur.text, replacement: nextNext.text, kind: "sub" });
        merged.push(next);
        k += 2;
        continue;
      }
      // Case 2: del immediately followed by add (no whitespace) → substitution
      if (cur.kind === "del" && cur.text.trim() && next && next.kind === "add" && next.text.trim()) {
        merged.push({ text: cur.text, replacement: next.text, kind: "sub" });
        k += 1;
        continue;
      }
      merged.push(cur);
    }
    return merged;
  };

  // Find confidence score for a word by locating it in the B segments.
  const confidenceForWord = (word, segments) => {
    if (!word || !segments || segments.length === 0) return null;
    const needle = word.trim().toLowerCase();
    for (const s of segments) {
      if ((s.text || "").toLowerCase().includes(needle)) {
        return typeof s.confidence === "number" ? s.confidence : null;
      }
    }
    return null;
  };

  const confidenceRingClass = (score) => {
    if (score == null) return "";
    if (score >= 0.85) return "ring-1 ring-emerald-400";
    if (score >= 0.65) return "ring-1 ring-amber-400";
    return "ring-1 ring-rose-500";
  };

  const renderDiff = (a, b, showAdds, segments) => {
    const tokens = diffWords(a, b);
    return tokens.map((t, k) => {
      if (t.kind === "same") return <span key={k}>{t.text}</span>;
      // Substitution : fond ambre + tooltip. Confidence ring (vert/ambre/rouge)
      // basé sur avg_logprob du segment Whisper de ce mot.
      if (t.kind === "sub") {
        const shownText = showAdds ? t.replacement : t.text;
        const otherText = showAdds ? t.text : t.replacement;
        const conf = confidenceForWord(shownText, segments);
        const tip = (showAdds ? `avant: ${otherText}` : `après: ${otherText}`) +
          (conf != null ? ` · confiance ${(conf * 100).toFixed(0)}%` : "");
        return (
          <span
            key={k}
            className={`bg-amber-100 text-amber-900 rounded px-0.5 decoration-amber-400 decoration-wavy underline ${confidenceRingClass(conf)}`}
            title={tip}
            data-testid="ab-diff-sub"
            data-confidence={conf ?? ""}
          >
            {shownText}
          </span>
        );
      }
      if (t.kind === "del" && !showAdds) return <span key={k} className="bg-rose-100 text-rose-800 rounded px-0.5" data-testid="ab-diff-del">{t.text}</span>;
      if (t.kind === "add" && showAdds) return <span key={k} className="bg-emerald-100 text-emerald-800 rounded px-0.5" data-testid="ab-diff-add">{t.text}</span>;
      return null;
    });
  };

  const adoptVersion = async (which) => {
    if (which === "b") {
      setWhisper((w) => ({ ...w, prompt: abPromptB }));
      toast.success(lang === "fr" ? "Prompt B adopté" : "Prompt B adopted");
      // B728 enhancement (Eric 2026-02-13) — Prompt B gagne le duel → offre de
      // le sauvegarder dans la bibliothèque en tant que « winner ». Ouvre le
      // dialog de save pré-rempli.
      setSaveName(lang === "fr" ? `Gagnant A/B ${new Date().toLocaleDateString("fr-CA")}` : `A/B winner ${new Date().toLocaleDateString("en-CA")}`);
      // Défère : laisse d'abord setWhisper commit avant d'ouvrir le dialog.
      setTimeout(() => setSaveDialogOpen(true), 100);
    } else {
      toast.info(lang === "fr" ? "Prompt A conservé" : "Prompt A kept");
    }
    setAbOpen(false);
    setAbResult(null);
  };

  const resetDefaults = () => {
    if (!defaults) return;
    if (engine === "whisper") {
      const d = { ...defaults.whisper };
      if (!d.language) d.language = "auto";
      setWhisper(d);
    } else {
      setAai({ ...defaults.assemblyai });
    }
  };

  if (!open) return null;
  const isWhisper = engine === "whisper";
  const title = isWhisper
    ? (lang === "fr" ? "Réglages Whisper" : "Whisper settings")
    : (lang === "fr" ? "Réglages Assembly AI" : "Assembly AI settings");

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`transcription-settings-modal-${engine}`}
    >
      <div
        className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-zinc-900">{title}</h3>
            <p className="text-xs text-zinc-500 mt-1">
              {lang === "fr"
                ? "Réglages appliqués UNIQUEMENT à cette vidéo, consommés à la prochaine transcription."
                : "Settings apply to this video only, used on the next transcription."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-100 rounded text-zinc-500 hover:text-zinc-800"
            data-testid="transcription-settings-close-btn"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {loading || !whisper || !aai ? (
          <p className="text-sm text-zinc-500 py-8 text-center">
            {lang === "fr" ? "Chargement…" : "Loading…"}
          </p>
        ) : isWhisper ? (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr" ? "Langue" : "Language"}
              </label>
              <select
                value={whisper.language || "auto"}
                onChange={(e) => setWhisper((w) => ({ ...w, language: e.target.value }))}
                className="w-full text-sm border border-zinc-300 rounded px-3 py-2 bg-white"
                data-testid="whisper-language-select"
              >
                {WHISPER_LANGS.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
              <p className="text-[10.5px] text-zinc-500 mt-1">
                {lang === "fr"
                  ? "Auto = Whisper détecte automatiquement (moins précis pour l'accent québécois)."
                  : "Auto = Whisper detects automatically (less accurate for Quebec French)."}
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr" ? "Prompt d'ancrage (contexte de domaine)" : "Initial prompt (domain context)"}
              </label>
              <textarea
                value={whisper.prompt || ""}
                onChange={(e) => setWhisper((w) => ({ ...w, prompt: e.target.value }))}
                rows={3}
                maxLength={800}
                className="w-full text-sm border border-zinc-300 rounded px-3 py-2 resize-y font-mono"
                data-testid="whisper-prompt-input"
                placeholder={defaults?.whisper?.prompt}
              />
              <p className="text-[10.5px] text-zinc-500 mt-1">
                {lang === "fr"
                  ? "Court et neutre — un prompt long peut se retrouver « halluciné » dans les silences (bug OpenAI Whisper). Max 800 caractères."
                  : "Keep short — long prompts get hallucinated in silences. Max 800 chars."}
              </p>
              {/* B728 enhancement (Eric 2026-02-13) — Suggestions IA :
                  puces cliquables générées à partir des métadonnées vidéo +
                  dictionnaire jargon de l'org. Zéro appel LLM, déterministe. */}
              {/* B728 enhancement (Eric 2026-02-13) — Prompt Library + Save.
                  Deux boutons compact au-dessus du textarea permettent de
                  charger un prompt éprouvé ou de sauver le prompt courant. */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setLibraryOpen((v) => !v)}
                    className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 font-semibold"
                    data-testid="prompt-library-toggle-btn"
                  >
                    <BookBookmark size={12} weight="bold" />
                    {lang === "fr" ? `Bibliothèque (${library.length})` : `Library (${library.length})`}
                  </button>
                  {libraryOpen && (
                    <div className="absolute z-10 mt-1 left-0 w-96 max-h-72 overflow-y-auto bg-white border border-zinc-300 rounded-lg shadow-xl p-2" data-testid="prompt-library-dropdown">
                      {library.length === 0 ? (
                        <p className="text-[11px] text-zinc-500 italic p-2">
                          {lang === "fr" ? "Bibliothèque vide. Sauvegardez un prompt pour l'utiliser sur d'autres vidéos." : "Empty library."}
                        </p>
                      ) : library.map((p) => (
                        <div key={p.id} className="p-1.5 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50" data-testid={`library-item-${p.id.slice(0, 8)}`}>
                          <div className="flex items-center gap-1 mb-0.5">
                            {p.winner_of_ab && <span title="Gagnant A/B">🏆</span>}
                            <span className="text-[11px] font-semibold text-zinc-800 truncate flex-1">{p.name}</span>
                            <span className="text-[10px] text-zinc-500 shrink-0">×{p.usage_count}</span>
                          </div>
                          <p className="text-[10.5px] text-zinc-600 font-mono line-clamp-1">{p.prompt}</p>
                          {p.tags?.length > 0 && (
                            <p className="text-[10px] text-zinc-500 mt-0.5">{p.tags.map((t) => `#${t}`).join(" ")}</p>
                          )}
                          <div className="flex items-center gap-1 mt-1">
                            <button type="button" onClick={() => applyLibraryPrompt(p, "A")} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 font-semibold" data-testid={`library-use-a-${p.id.slice(0, 8)}`}>
                              {lang === "fr" ? "→ A" : "→ A"}
                            </button>
                            <button type="button" onClick={() => applyLibraryPrompt(p, "B")} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold" data-testid={`library-use-b-${p.id.slice(0, 8)}`}>
                              {lang === "fr" ? "→ B" : "→ B"}
                            </button>
                            <button type="button" onClick={() => deleteLibraryPrompt(p.id)} className="ml-auto text-[10px] px-1 py-0.5 rounded text-red-500 hover:bg-red-50" data-testid={`library-delete-${p.id.slice(0, 8)}`}>×</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setSaveName(""); setSaveDialogOpen(true); }}
                  className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 font-semibold"
                  data-testid="prompt-library-save-btn"
                  disabled={!(whisper.prompt || "").trim()}
                >
                  <FloppyDisk size={12} weight="bold" />
                  {lang === "fr" ? "Sauver ce prompt" : "Save prompt"}
                </button>
                {/* B728 enhancement (Eric 2026-02-13) — Version history dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setHistoryOpen((v) => !v)}
                    className="inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 font-semibold"
                    data-testid="prompt-history-toggle-btn"
                    disabled={history.length === 0}
                    title={history.length === 0 ? "Aucune version antérieure" : "Historique du prompt"}
                  >
                    <ArrowCounterClockwise size={12} weight="bold" />
                    {lang === "fr" ? `Historique (${history.length})` : `History (${history.length})`}
                  </button>
                  {historyOpen && history.length > 0 && (
                    <div className="absolute z-10 mt-1 right-0 w-96 max-h-72 overflow-y-auto bg-white border border-zinc-300 rounded-lg shadow-xl p-2" data-testid="prompt-history-dropdown">
                      {history.map((h) => (
                        <div key={h.id} className="p-1.5 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50" data-testid={`history-entry-${h.id.slice(0, 8)}`}>
                          <div className="flex items-center gap-2 text-[10.5px] text-zinc-600">
                            <span className="font-semibold">{h.saved_by_name || h.saved_by?.slice(0, 8)}</span>
                            <span>·</span>
                            <span>{new Date(h.saved_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</span>
                          </div>
                          <p className="text-[10.5px] text-zinc-700 font-mono line-clamp-2 mt-0.5">{h.prev_prompt}</p>
                          <button
                            type="button"
                            onClick={() => openHistoryPreview(h)}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold mt-1"
                            data-testid={`restore-history-${h.id.slice(0, 8)}`}
                          >
                            ↩ {lang === "fr" ? "Prévisualiser & restaurer" : "Preview & restore"}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {saveDialogOpen && (
                <div className="mt-2 p-2 border border-blue-200 bg-blue-50 rounded" data-testid="prompt-save-dialog">
                  <label className="block text-[10.5px] font-semibold text-zinc-700 mb-0.5">
                    {lang === "fr" ? "Nom (identifiant lisible)" : "Name"}
                  </label>
                  <input
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder={lang === "fr" ? "Ex: REER + CELIAPP + jargon Vincent" : "e.g. REER + jargon"}
                    className="w-full text-[12px] border border-zinc-300 rounded px-2 py-1 mb-1"
                    data-testid="prompt-save-name-input"
                  />
                  <label className="block text-[10.5px] font-semibold text-zinc-700 mb-0.5">
                    {lang === "fr" ? "Étiquettes (séparées par virgules)" : "Tags (comma-separated)"}
                  </label>
                  <input
                    value={saveTagsRaw}
                    onChange={(e) => setSaveTagsRaw(e.target.value)}
                    placeholder="finance, quebec, jargon"
                    className="w-full text-[12px] border border-zinc-300 rounded px-2 py-1 mb-1"
                    data-testid="prompt-save-tags-input"
                  />
                  <div className="flex items-center gap-1 justify-end">
                    <button type="button" onClick={() => setSaveDialogOpen(false)} className="text-[11px] px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-50">
                      {lang === "fr" ? "Annuler" : "Cancel"}
                    </button>
                    <button type="button" onClick={() => saveToLibrary({ winnerOfAb: false })} className="text-[11px] px-2 py-1 rounded bg-[#002FA7] hover:bg-[#001F7A] text-white font-semibold" data-testid="prompt-save-confirm-btn">
                      {lang === "fr" ? "Sauver" : "Save"}
                    </button>
                  </div>
                </div>
              )}
              {suggestions && suggestions.length > 0 && (
                <div className="mt-2" data-testid="whisper-prompt-suggestions">
                  <p className="text-[10.5px] font-semibold text-zinc-600 mb-1 inline-flex items-center gap-1">
                    <Sparkle size={12} weight="fill" className="text-amber-500" />
                    {lang === "fr" ? "Suggestions intelligentes :" : "Smart suggestions:"}
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => applySuggestion(s)}
                        className="text-left text-[11px] px-2 py-1.5 rounded border border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-300 transition-colors"
                        data-testid={`whisper-prompt-suggestion-${i}`}
                        title={s.why}
                      >
                        <span className="font-semibold text-amber-900">{s.label}</span>
                        <span className="block text-zinc-700 mt-0.5 line-clamp-2 font-mono">{s.prompt}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* B728 enhancement (Eric 2026-02-13) — Prompt A/B Test.
                  Bouton pour ouvrir le panneau de comparaison entre le prompt
                  actuel (A) et un candidat (B) sur un court extrait. */}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setAbOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#002FA7] hover:underline"
                  data-testid="whisper-ab-toggle-btn"
                >
                  <GitDiff size={12} weight="bold" />
                  {abOpen
                    ? (lang === "fr" ? "Masquer l'A/B test" : "Hide A/B test")
                    : (lang === "fr" ? "Tester ce prompt A/B contre une variante" : "A/B test this prompt vs. a variant")}
                </button>
                {abOpen && (
                  <div className="mt-2 p-3 rounded border border-zinc-200 bg-zinc-50 space-y-2" data-testid="whisper-ab-panel">
                    <div>
                      <label className="block text-[10.5px] font-semibold text-zinc-700 mb-0.5">
                        {lang === "fr" ? "Prompt B (candidat)" : "Prompt B (candidate)"}
                      </label>
                      <textarea
                        value={abPromptB}
                        onChange={(e) => setAbPromptB(e.target.value)}
                        rows={2}
                        maxLength={800}
                        className="w-full text-[12px] border border-zinc-300 rounded px-2 py-1.5 resize-y font-mono"
                        placeholder={lang === "fr" ? "Ex: Formation FR-QC. Vocabulaire : REER, CELIAPP, RAP..." : "e.g. Financial training..."}
                        data-testid="whisper-ab-prompt-b-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-[10.5px] font-semibold text-zinc-700">
                        {lang === "fr" ? `Durée échantillon (${abSampleSec}s)` : `Sample duration (${abSampleSec}s)`}
                        <input
                          type="range" min="10" max="60" step="5"
                          value={abSampleSec}
                          onChange={(e) => setAbSampleSec(parseInt(e.target.value, 10))}
                          className="w-full"
                          data-testid="whisper-ab-sample-input"
                        />
                      </label>
                      <label className="text-[10.5px] font-semibold text-zinc-700">
                        {lang === "fr" ? "Offset départ (s)" : "Start offset (s)"}
                        <div className="flex items-center gap-1">
                          <input
                            type="number" min="0" step="10"
                            value={abStartSec}
                            onChange={(e) => setAbStartSec(Math.max(0, parseInt(e.target.value || "0", 10)))}
                            className="w-full text-[12px] border border-zinc-300 rounded px-2 py-1"
                            data-testid="whisper-ab-start-input"
                          />
                          <button
                            type="button"
                            onClick={autoPickDenseZone}
                            disabled={autoPickLoading}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 font-semibold shrink-0 disabled:opacity-50"
                            data-testid="whisper-ab-autopick-btn"
                            title={lang === "fr" ? "Trouver la zone la plus dense en mots" : "Find densest zone"}
                          >
                            <Target size={11} weight="bold" />
                            {autoPickLoading ? "…" : (lang === "fr" ? "Auto" : "Auto")}
                          </button>
                        </div>
                      </label>
                    </div>
                    {/* Vincent B778 v3 (Eric 2026-02-18 SPEC STRICK PROD) —
                        Silence Preview Meter : mini barre horizontale qui
                        montre où sont les zones parlées (bleu) vs silences
                        (gris) sur toute la durée. Vincent clique une barre
                        → l'offset A/B se cale sur ce bucket. La fenêtre
                        active (offset → offset+sample) est surlignée en
                        ambre pour visualiser exactement ce qu'on va
                        transcrire. */}
                    {energyMap && energyMap.buckets && energyMap.buckets.length > 0 && (
                      <div className="mt-1" data-testid="whisper-ab-energy-meter">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] text-zinc-500">
                            {lang === "fr" ? "Densité de parole (clique pour caler l'offset)" : "Speech density (click to snap the offset)"}
                          </span>
                          <span className="text-[10px] text-zinc-400 tabular-nums">
                            {Math.floor((energyMap.duration || 0) / 60)}m {Math.round((energyMap.duration || 0) % 60)}s
                          </span>
                        </div>
                        <div
                          className="flex items-end gap-[1px] h-8 w-full bg-zinc-100 rounded overflow-hidden border border-zinc-200 cursor-pointer"
                          title={lang === "fr"
                            ? "Clique une barre pour choisir un offset. Bleu = parole dense, gris = silence."
                            : "Click a bar to pick an offset. Blue = dense speech, gray = silence."}
                        >
                          {energyMap.buckets.map((b, i) => {
                            const isActive = abStartSec >= b.start && abStartSec < b.end;
                            const winStart = abStartSec;
                            const winEnd = abStartSec + abSampleSec;
                            const inWindow = b.end > winStart && b.start < winEnd;
                            // Hauteur : min 2px pour toujours voir un ghost
                            const h = 2 + Math.round(b.score * 30);
                            // Couleur : bleu Knoria pour parole dense, gris pour silence
                            let bg;
                            if (b.score < 0.15) bg = "bg-zinc-300";
                            else if (b.score < 0.4) bg = "bg-blue-200";
                            else if (b.score < 0.7) bg = "bg-blue-400";
                            else bg = "bg-blue-600";
                            // Surligne la fenêtre A/B (background ambre translucide)
                            const overlay = inWindow ? "ring-1 ring-amber-400 ring-inset" : "";
                            const active = isActive ? "outline outline-1 outline-amber-600" : "";
                            return (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setAbStartSec(Math.round(b.start))}
                                className={`flex-1 min-w-0 ${bg} ${overlay} ${active} transition-colors hover:opacity-80`}
                                style={{ height: `${h}px` }}
                                data-testid={`whisper-ab-energy-bar-${i}`}
                                data-score={b.score}
                                data-start={b.start}
                                title={`${Math.floor(b.start / 60)}:${String(Math.round(b.start % 60)).padStart(2, "0")} — ${lang === "fr" ? "densité" : "density"} ${(b.score * 100).toFixed(0)}%`}
                                aria-label={`${lang === "fr" ? "Choisir l'offset" : "Pick offset"} ${Math.round(b.start)}s`}
                              />
                            );
                          })}
                        </div>
                        <div className="flex justify-between text-[9px] text-zinc-400 tabular-nums mt-0.5">
                          <span>0:00</span>
                          <span>
                            {lang === "fr" ? "Source : " : "Source: "}
                            <span className="font-mono" data-testid="whisper-ab-energy-source">{energyMap.source || "empty"}</span>
                          </span>
                          <span>
                            {Math.floor((energyMap.duration || 0) / 60)}:{String(Math.round((energyMap.duration || 0) % 60)).padStart(2, "0")}
                          </span>
                        </div>
                      </div>
                    )}
                    {energyMap && energyMap.source === "empty" && (
                      <p className="text-[10px] text-zinc-500 italic" data-testid="whisper-ab-energy-empty-note">
                        {lang === "fr"
                          ? "ℹ️ Pas de transcription existante — la carte de densité s'affichera après la 1ʳᵉ transcription complète."
                          : "ℹ️ No transcript yet — the density map will appear after the first full transcription."}
                      </p>
                    )}
                    <p className="text-[10.5px] text-zinc-500 italic">
                      {lang === "fr"
                        ? `Coût estimé ≈ ${(abSampleSec / 60 * 0.006 * 2).toFixed(4)} $ (${abSampleSec}s × 2 prompts).`
                        : `Est. cost ≈ $${(abSampleSec / 60 * 0.006 * 2).toFixed(4)} (${abSampleSec}s × 2 prompts).`}
                    </p>
                    <button
                      type="button"
                      onClick={runAbTest}
                      disabled={abLoading || !abPromptB.trim()}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded bg-[#002FA7] hover:bg-[#001F7A] text-white disabled:opacity-50"
                      data-testid="whisper-ab-run-btn"
                    >
                      <GitDiff size={12} weight="bold" />
                      {abLoading ? "…" : (lang === "fr" ? "Comparer" : "Compare")}
                    </button>
                    {abResult && (
                      <div className="grid grid-cols-2 gap-2 mt-2" data-testid="whisper-ab-result">
                        {/* Vincent B778 (Eric 2026-02-18 SPEC STRICK PROD) —
                            Bannière d'alerte si Whisper a halluciné (silence
                            dominant, boucle de tokens, répétition dominante).
                            Prévient l'utilisateur AVANT qu'il ne prenne une
                            décision A/B sur un texte non-représentatif. */}
                        {(abResult.a?.hallucination_detected || abResult.b?.hallucination_detected) && (
                          <div
                            className="col-span-2 p-2.5 border border-amber-300 bg-amber-50 rounded text-[11px] text-amber-900"
                            data-testid="whisper-ab-hallucination-banner"
                          >
                            <p className="font-bold flex items-center gap-1.5 mb-1">
                              <span aria-hidden="true">⚠️</span>
                              {lang === "fr"
                                ? "Hallucination Whisper détectée sur cet extrait"
                                : "Whisper hallucination detected on this clip"}
                            </p>
                            <p className="leading-snug">
                              {lang === "fr"
                                ? "L'extrait audio choisi contient trop de silence ou de bruit. Whisper s'est rabattu sur les mots du prompt et les a répétés au lieu de transcrire de la parole. Essayez un autre offset (ex. + 60 s) ou un extrait plus long."
                                : "The audio clip is mostly silent or noisy. Whisper fell back to repeating tokens from the prompt instead of transcribing speech. Try a different start offset (e.g. + 60 s) or a longer sample."}
                            </p>
                            <div className="mt-1.5 text-[10px] text-amber-800 space-x-3">
                              {abResult.a?.hallucination_detected && (
                                <span data-testid="whisper-ab-hallucination-a">
                                  {lang === "fr" ? "A : " : "A: "}
                                  {abResult.a.hallucination_kind === "silence_dominant" && (lang === "fr" ? "silence dominant" : "silence dominant")}
                                  {abResult.a.hallucination_kind === "token_repetition" && `« ${abResult.a.hallucination_meta?.dominant_token} » × ${abResult.a.hallucination_meta?.occurrences}`}
                                  {abResult.a.hallucination_kind === "loop_at_end" && (lang === "fr" ? "boucle finale" : "loop at end")}
                                </span>
                              )}
                              {abResult.b?.hallucination_detected && (
                                <span data-testid="whisper-ab-hallucination-b">
                                  {lang === "fr" ? "B : " : "B: "}
                                  {abResult.b.hallucination_kind === "silence_dominant" && (lang === "fr" ? "silence dominant" : "silence dominant")}
                                  {abResult.b.hallucination_kind === "token_repetition" && `« ${abResult.b.hallucination_meta?.dominant_token} » × ${abResult.b.hallucination_meta?.occurrences}`}
                                  {abResult.b.hallucination_kind === "loop_at_end" && (lang === "fr" ? "boucle finale" : "loop at end")}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="p-2 border border-zinc-200 bg-white rounded">
                          <p className="text-[10.5px] font-bold text-rose-700 mb-1">
                            A ({lang === "fr" ? "actuel" : "current"})
                            {abResult.a?.hallucination_detected && (
                              <span className="ml-1.5 text-amber-700 font-semibold" title={lang === "fr" ? "Résultat hallucinatoire — voir bannière" : "Hallucinated result — see banner"}>
                                ⚠️
                              </span>
                            )}
                          </p>
                          <p className="text-[11px] leading-snug whitespace-pre-wrap" data-testid="whisper-ab-result-a">
                            {renderDiff(abResult.a?.text, abResult.b?.text, false, abResult.a?.segments)}
                          </p>
                        </div>
                        <div className="p-2 border border-zinc-200 bg-white rounded">
                          <p className="text-[10.5px] font-bold text-emerald-700 mb-1">
                            B ({lang === "fr" ? "candidat" : "candidate"})
                            {abResult.b?.hallucination_detected && (
                              <span className="ml-1.5 text-amber-700 font-semibold" title={lang === "fr" ? "Résultat hallucinatoire — voir bannière" : "Hallucinated result — see banner"}>
                                ⚠️
                              </span>
                            )}
                          </p>
                          <p className="text-[11px] leading-snug whitespace-pre-wrap" data-testid="whisper-ab-result-b">
                            {renderDiff(abResult.a?.text, abResult.b?.text, true, abResult.b?.segments)}
                          </p>
                        </div>
                        <div className="col-span-2 flex items-center justify-end gap-2 pt-1">
                          {/* B769 (Eric 2026-02-17) — Nudge "Adopt B looks safe"
                              quand ≥90% des substitutions B ont confidence >0.85.
                              Subtil : petit badge vert-émeraude, non intrusif. */}
                          {(() => {
                            const _bSubs = diffWords(abResult.a?.text, abResult.b?.text)
                              .filter((t) => t.kind === "sub")
                              .map((t) => confidenceForWord(t.replacement, abResult.b?.segments))
                              .filter((c) => c != null);
                            if (_bSubs.length < 3) return null; // évite les stats sur trop peu de subs
                            const _safe = _bSubs.filter((c) => c > 0.85).length;
                            const _ratio = _safe / _bSubs.length;
                            if (_ratio < 0.9) return null;
                            const _pct = Math.round(_ratio * 100);
                            return (
                              <span
                                className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-0.5 mr-auto"
                                data-testid="ab-adopt-b-safe-nudge"
                                data-ratio={_ratio.toFixed(2)}
                                title={lang === "fr"
                                  ? `${_pct}% des substitutions B ont une confiance > 0.85 (${_safe}/${_bSubs.length}).`
                                  : `${_pct}% of B substitutions have confidence > 0.85 (${_safe}/${_bSubs.length}).`}
                              >
                                <span aria-hidden="true">✅</span>
                                {lang === "fr" ? "Adopter B semble sûr" : "Adopt B looks safe"}
                              </span>
                            );
                          })()}
                          <button
                            type="button"
                            onClick={() => adoptVersion("a")}
                            className="text-[11px] px-3 py-1 rounded border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-800 font-semibold"
                            data-testid="whisper-ab-keep-a-btn"
                          >
                            {lang === "fr" ? "Garder A" : "Keep A"}
                          </button>
                          <button
                            type="button"
                            onClick={() => adoptVersion("b")}
                            className="text-[11px] px-3 py-1 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold"
                            data-testid="whisper-ab-adopt-b-btn"
                          >
                            {lang === "fr" ? "Adopter B" : "Adopt B"}
                          </button>
                        </div>
                        {/* B728 v4 (Eric 2026-02-13) — Confidence Sort panel :
                            liste les substitutions triées par confiance ASC
                            (les plus risquées en premier). Aide les admins à
                            scanner d'abord les corrections de jargon douteuses. */}
                        {(() => {
                          const subs = diffWords(abResult.a?.text, abResult.b?.text)
                            .filter((t) => t.kind === "sub")
                            .map((t) => ({
                              ...t,
                              confidence: confidenceForWord(t.replacement, abResult.b?.segments),
                            }))
                            .sort((x, y) => (x.confidence ?? 1) - (y.confidence ?? 1));
                          if (subs.length === 0) return null;
                          return (
                            <div className="col-span-2 mt-2 p-2 border border-amber-200 bg-amber-50 rounded" data-testid="ab-confidence-sort-panel">
                              <p className="text-[10.5px] font-bold text-amber-900 mb-1">
                                🔎 {lang === "fr" ? `Substitutions triées par risque (${subs.length})` : `Substitutions sorted by risk (${subs.length})`}
                              </p>
                              <ul className="space-y-0.5">
                                {subs.slice(0, 10).map((s, i) => {
                                  const conf = s.confidence;
                                  const pct = conf != null ? `${(conf * 100).toFixed(0)}%` : "?";
                                  const dot = conf == null ? "text-zinc-400" : conf >= 0.85 ? "text-emerald-500" : conf >= 0.65 ? "text-amber-500" : "text-rose-500";
                                  return (
                                    <li key={i} className="text-[11px] flex items-center gap-2" data-testid={`ab-risk-row-${i}`}>
                                      <span className={`${dot} shrink-0 font-bold`}>●</span>
                                      <span className="font-mono text-rose-700 line-through">{s.text}</span>
                                      <span className="text-zinc-500">→</span>
                                      <span className="font-mono text-emerald-700 font-semibold">{s.replacement}</span>
                                      <span className="ml-auto text-[10px] text-zinc-600 shrink-0" data-testid={`ab-risk-conf-${i}`}>{pct}</span>
                                    </li>
                                  );
                                })}
                                {subs.length > 10 && (
                                  <li className="text-[10px] text-zinc-500 italic">
                                    {lang === "fr" ? `+${subs.length - 10} autres…` : `+${subs.length - 10} more…`}
                                  </li>
                                )}
                              </ul>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {/* B728 enhancement (Eric 2026-02-13) — Batch A/B test.
                        Étend la comparaison à d'autres vidéos pour repérer
                        les motifs récurrents avant de committer un prompt. */}
                    <div className="pt-2 mt-2 border-t border-zinc-200">
                      <button
                        type="button"
                        onClick={() => (batchOpen ? setBatchOpen(false) : openBatchPanel())}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#002FA7] hover:underline"
                        data-testid="whisper-batch-toggle-btn"
                      >
                        <Stack size={12} weight="bold" />
                        {batchOpen
                          ? (lang === "fr" ? "Masquer le batch" : "Hide batch")
                          : (lang === "fr" ? "Étendre le test à d'autres vidéos (Batch)" : "Extend to other videos (Batch)")}
                      </button>
                      {batchOpen && (
                        <div className="mt-2" data-testid="whisper-batch-panel">
                          <p className="text-[10.5px] text-zinc-600 mb-1">
                            {lang === "fr"
                              ? `Choisis 1-4 vidéos supplémentaires (max 5 total avec l'actuelle). Coût ≈ ${(abSampleSec / 60 * 0.006 * 2 * (batchSelected.length + 1)).toFixed(4)} $.`
                              : `Pick 1-4 additional videos. Cost ≈ $${(abSampleSec / 60 * 0.006 * 2 * (batchSelected.length + 1)).toFixed(4)}.`}
                          </p>
                          <div className="max-h-40 overflow-y-auto border border-zinc-200 rounded bg-white">
                            {batchVideos.length === 0 ? (
                              <p className="text-[11px] text-zinc-500 italic p-2">
                                {lang === "fr" ? "Aucune autre vidéo complète disponible." : "No other complete videos."}
                              </p>
                            ) : batchVideos.map((v) => (
                              <label
                                key={v.id}
                                className="flex items-center gap-2 p-1.5 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 cursor-pointer"
                                data-testid={`batch-video-row-${v.id.slice(0, 8)}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={batchSelected.includes(v.id)}
                                  onChange={() => toggleBatchSelected(v.id)}
                                  data-testid={`batch-video-checkbox-${v.id.slice(0, 8)}`}
                                />
                                <span className="text-[11px] text-zinc-800 truncate">{v.title}</span>
                              </label>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={runBatchTest}
                            disabled={batchLoading || batchSelected.length === 0}
                            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded bg-[#002FA7] hover:bg-[#001F7A] text-white disabled:opacity-50"
                            data-testid="whisper-batch-run-btn"
                          >
                            <Stack size={12} weight="bold" />
                            {batchLoading ? "…" : (lang === "fr" ? `Lancer le batch (${batchSelected.length + 1})` : `Run batch (${batchSelected.length + 1})`)}
                          </button>
                          {batchResult && (
                            <div className="mt-2 max-h-60 overflow-y-auto" data-testid="whisper-batch-result">
                              {batchResult.results.map((r, i) => (
                                <div key={i} className="mb-2 p-2 border border-zinc-200 rounded bg-white text-[11px]">
                                  <p className="font-semibold text-zinc-800 truncate">
                                    {r.status === "ok" ? "✓" : "✗"} {r.title || r.video_id.slice(0, 12)}
                                    {r.detail && <span className="ml-1 text-red-500 italic">({r.detail})</span>}
                                  </p>
                                  {r.status === "ok" && r.a && (
                                    <>
                                      <p className="text-rose-700 font-semibold mt-1">A :</p>
                                      <p className="whitespace-pre-wrap">{renderDiff(r.a.text, r.b?.text || r.a.text, false)}</p>
                                      {r.b && (
                                        <>
                                          <p className="text-emerald-700 font-semibold mt-1">B :</p>
                                          <p className="whitespace-pre-wrap">{renderDiff(r.a.text, r.b.text, true)}</p>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr" ? "Température" : "Temperature"} ({whisper.temperature?.toFixed(2) ?? "0.00"})
              </label>
              <input
                type="range"
                min="0" max="1" step="0.05"
                value={whisper.temperature ?? 0}
                onChange={(e) => setWhisper((w) => ({ ...w, temperature: parseFloat(e.target.value) }))}
                className="w-full"
                data-testid="whisper-temperature-input"
              />
              <p className="text-[10.5px] text-zinc-500 mt-1">
                {lang === "fr"
                  ? "0.0 = déterministe (recommandé, minimise les boucles de répétition). >0 = plus créatif mais plus d'hallucinations."
                  : "0.0 = deterministic (recommended). Higher = more creative but more hallucinations."}
              </p>
            </div>
            {/* Vincent B821 (Eric 2026-02-13) — Durée cible des paragraphes
                après regroupement Whisper. Plage 30-90s, défaut 45s. */}
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr"
                  ? `Durée cible des paragraphes : ${Math.round(whisper.paragraph_target_seconds ?? 45)} s`
                  : `Paragraph target duration: ${Math.round(whisper.paragraph_target_seconds ?? 45)} s`}
              </label>
              <input
                type="range"
                min="30" max="90" step="1"
                value={whisper.paragraph_target_seconds ?? 45}
                onChange={(e) => setWhisper((w) => ({ ...w, paragraph_target_seconds: parseFloat(e.target.value) }))}
                className="w-full"
                data-testid="whisper-paragraph-target-input"
              />
              <div className="flex justify-between text-[10px] text-zinc-400 -mt-0.5 px-0.5">
                <span>30 s</span><span className="font-semibold text-teal-700">40-50 s (recommandé)</span><span>90 s</span>
              </div>
              <p className="text-[10.5px] text-zinc-500 mt-1">
                {lang === "fr"
                  ? "Whisper regroupe les micro-segments en paragraphes de cette durée cible (coupure à la fin de phrase suivante). Vincent recommande 40-50 s pour un rythme de lecture confortable."
                  : "Whisper groups micro-segments into paragraphs of this target duration (cut at next sentence end)."}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr" ? "Langue" : "Language"}
              </label>
              <select
                value={aai.language_code || "fr"}
                onChange={(e) => setAai((a) => ({ ...a, language_code: e.target.value }))}
                className="w-full text-sm border border-zinc-300 rounded px-3 py-2 bg-white"
                data-testid="aai-language-select"
              >
                {AAI_LANGS.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr" ? "Modèle principal" : "Primary model"}
              </label>
              <select
                value={aai.speech_model_primary || "universal-3-pro"}
                onChange={(e) => setAai((a) => ({ ...a, speech_model_primary: e.target.value }))}
                className="w-full text-sm border border-zinc-300 rounded px-3 py-2 bg-white"
                data-testid="aai-model-select"
              >
                <option value="universal-3-pro">universal-3-pro (recommandé FR natif)</option>
                <option value="universal-2">universal-2 (fallback)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr" ? "Nombre de locuteurs attendus" : "Speakers expected"}
              </label>
              <input
                type="number" min="1" max="10"
                value={aai.speakers_expected ?? ""}
                onChange={(e) => setAai((a) => ({ ...a, speakers_expected: e.target.value ? parseInt(e.target.value, 10) : null }))}
                placeholder={lang === "fr" ? "Auto (basé sur les locuteurs cochés)" : "Auto (based on checked speakers)"}
                className="w-full text-sm border border-zinc-300 rounded px-3 py-2"
                data-testid="aai-speakers-expected-input"
              />
              <p className="text-[10.5px] text-zinc-500 mt-1">
                {lang === "fr"
                  ? "Laisser vide = déterminé automatiquement selon les locuteurs cochés. Améliore le clustering (évite « 2 vrais → 4 IDs »)."
                  : "Leave empty for auto based on checked speakers. Improves clustering."}
              </p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!aai.disfluencies}
                onChange={(e) => setAai((a) => ({ ...a, disfluencies: e.target.checked }))}
                className="mt-0.5"
                data-testid="aai-disfluencies-checkbox"
              />
              <span className="text-xs text-zinc-700">
                <strong>{lang === "fr" ? "Conserver les hésitations" : "Keep disfluencies"}</strong>
                <span className="block text-[10.5px] text-zinc-500">
                  {lang === "fr"
                    ? "« euh », « ben »… — marqueurs utiles de tour de parole."
                    : "\"uh\", \"um\"... — useful turn-taking markers."}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!aai.filter_profanity}
                onChange={(e) => setAai((a) => ({ ...a, filter_profanity: e.target.checked }))}
                className="mt-0.5"
                data-testid="aai-filter-profanity-checkbox"
              />
              <span className="text-xs text-zinc-700">
                <strong>{lang === "fr" ? "Filtrer les grossièretés" : "Filter profanity"}</strong>
                <span className="block text-[10.5px] text-zinc-500">
                  {lang === "fr"
                    ? "Remplace les jurons par des astérisques (généralement désactivé)."
                    : "Replace swear words with asterisks (usually off)."}
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-2 pt-3 border-t border-zinc-100">
          <button
            onClick={resetDefaults}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700"
            data-testid="transcription-settings-reset-btn"
            disabled={loading || saving || rerunning}
          >
            <ArrowCounterClockwise size={12} />
            {lang === "fr" ? "Rétablir les défauts" : "Reset to defaults"}
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700"
              data-testid="transcription-settings-cancel-btn"
              disabled={saving || rerunning}
            >
              {lang === "fr" ? "Annuler" : "Cancel"}
            </button>
            <button
              onClick={save}
              disabled={saving || rerunning || loading}
              className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 font-semibold disabled:opacity-50"
              data-testid="transcription-settings-save-btn"
            >
              <FloppyDisk size={12} weight="bold" />
              {saving ? "…" : (lang === "fr" ? "Enregistrer" : "Save")}
            </button>
            {/* B728 enhancement (Eric 2026-02-13) — « Enregistrer & relancer maintenant »
                déclenche immédiatement le pipeline correspondant (rerun-whisper
                pour Whisper, speakers/reprocess pour AssemblyAI). */}
            <button
              onClick={saveAndRerun}
              disabled={saving || rerunning || loading}
              className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded bg-[#002FA7] hover:bg-[#001F7A] text-white font-semibold shadow-sm disabled:opacity-50"
              data-testid="transcription-settings-save-rerun-btn"
              title={engine === "whisper"
                ? (lang === "fr" ? "Sauve et relance la transcription Whisper immédiatement" : "Save and re-run Whisper now")
                : (lang === "fr" ? "Sauve et relance la diarisation Assembly AI immédiatement" : "Save and re-run Assembly AI now")}
            >
              <Lightning size={12} weight="fill" />
              {rerunning ? "…" : (lang === "fr" ? "Enregistrer & relancer" : "Save & rerun now")}
            </button>
            {/* Vincent B779 (Eric 2026-02-18 SPEC STRICK PROD) — Bouton
                spécifique « Re-transcrire sans dedup ». Ne s'affiche que
                dans le modal Whisper (pas AssemblyAI). Cas d'usage :
                le diagnostic Whisper a rapporté `aggressive_dedup` et
                la transcription semble tronquée par la règle Jaccard.
                Style ambre pour signaler l'action « diagnostic ». */}
            {engine === "whisper" && (
              <button
                onClick={rerunSansDedup}
                disabled={saving || rerunning || loading}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-800 font-semibold shadow-sm disabled:opacity-50"
                data-testid="whisper-rerun-skip-dedup-btn"
                title={lang === "fr"
                  ? "Re-transcrire cette vidéo en désactivant le filtre anti-hallucination. Utile si le diagnostic a signalé aggressive_dedup."
                  : "Re-transcribe with the anti-hallucination filter OFF. Use if the diagnostic reported aggressive_dedup."}
              >
                <span aria-hidden="true">⚠️</span>
                {lang === "fr" ? "Re-transcrire sans dedup" : "Re-transcribe without dedup"}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* B728 v4 (Eric 2026-02-13) — History restore preview modal.
          Affiche un diff current↔historical avant de confirmer le rollback. */}
      {historyDiffPreview && (
        <div
          className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
          onClick={() => setHistoryDiffPreview(null)}
          data-testid="history-diff-preview"
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-zinc-900">
                  {lang === "fr" ? "Aperçu du rollback" : "Rollback preview"}
                </h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  {lang === "fr" ? "Vert = ajouté lors du rollback · Rouge = perdu" : "Green = restored · Red = lost"}
                  {" · "}{historyDiffPreview.entry.saved_by_name} · {new Date(historyDiffPreview.entry.saved_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}
                </p>
              </div>
              <button
                onClick={() => setHistoryDiffPreview(null)}
                className="p-1 hover:bg-zinc-100 rounded text-zinc-500"
                data-testid="history-diff-close-btn"
              >
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 border border-zinc-200 rounded bg-zinc-50">
                <p className="text-[10.5px] font-bold text-zinc-700 mb-1">
                  {lang === "fr" ? "Actuel (sera perdu)" : "Current (will be lost)"}
                </p>
                <p className="text-[12px] font-mono whitespace-pre-wrap leading-snug" data-testid="history-diff-current">
                  {renderDiff(historyDiffPreview.currentPrompt, historyDiffPreview.entry.prev_prompt, false, [])}
                </p>
              </div>
              <div className="p-2 border border-emerald-300 rounded bg-emerald-50">
                <p className="text-[10.5px] font-bold text-emerald-800 mb-1">
                  {lang === "fr" ? "Historique (à restaurer)" : "Historical (to restore)"}
                </p>
                <p className="text-[12px] font-mono whitespace-pre-wrap leading-snug" data-testid="history-diff-historical">
                  {renderDiff(historyDiffPreview.currentPrompt, historyDiffPreview.entry.prev_prompt, true, [])}
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
              <button
                onClick={() => setHistoryDiffPreview(null)}
                className="text-[11px] px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700"
                data-testid="history-diff-cancel-btn"
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </button>
              <button
                onClick={confirmRestoreHistorical}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-4 py-1.5 rounded bg-[#002FA7] hover:bg-[#001F7A] text-white"
                data-testid="history-diff-confirm-btn"
              >
                <ArrowCounterClockwise size={12} weight="bold" />
                {lang === "fr" ? "Confirmer le rollback" : "Confirm rollback"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptionSettingsModal;
